/** B2B invoice list (BP-09). Owner/manager only. Filterable by status,
 *  customer_tax_id, and issued-date range. Trilingual via billing.b2b.* keys. */
import { FileText } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmptyState,
  Money,
  SearchInput,
  Select,
  StatusPill,
  statusTone,
  Table,
  TableSkeleton,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui.js';

import { useB2BInvoices } from './hooks/useB2BInvoices.js';

const STATUSES = ['', 'draft', 'issued', 'sent', 'voided'] as const;

export function B2BInvoiceList(): ReactNode {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('');
  const [taxId, setTaxId] = useState('');

  const query = useB2BInvoices({
    ...(status ? { status: status as 'draft' | 'issued' | 'sent' | 'voided' } : {}),
    ...(taxId.trim() ? { customer_tax_id: taxId.trim() } : {}),
  });
  const rows = query.data ?? [];

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h3 className="flex-1 font-display text-base font-semibold text-fg">
          {t('billing.b2b.listTitle')}
        </h3>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('billing.b2b.filterStatus')}
          className="w-auto"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s ? t(`billing.b2b.status.${s}`) : t('billing.b2b.allStatuses')}
            </option>
          ))}
        </Select>
        <SearchInput
          placeholder={t('billing.b2b.customerTaxId')}
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          aria-label={t('billing.b2b.filterTaxId')}
          className="w-40"
        />
      </div>

      <TableWrap>
        <Table>
          <THead>
            <tr>
              <TH>{t('billing.b2b.invoiceNumber')}</TH>
              <TH>{t('billing.b2b.customerCompany')}</TH>
              <TH>{t('billing.b2b.customerTaxId')}</TH>
              <TH numeric>{t('billing.b2b.total')}</TH>
              <TH>{t('billing.b2b.statusLabel')}</TH>
            </tr>
          </THead>
          {query.isLoading ? (
            <TableSkeleton rows={4} cols={5} />
          ) : (
            <tbody>
              {rows.map((inv) => (
                <TR key={inv.id}>
                  <TD className="font-mono">{inv.invoice_number ?? '—'}</TD>
                  <TD className="font-medium">{inv.customer_company}</TD>
                  <TD className="text-muted">{inv.customer_tax_id}</TD>
                  <TD numeric>
                    <Money minor={inv.total} />
                  </TD>
                  <TD>
                    <StatusPill
                      status={inv.status}
                      tone={statusTone(inv.status)}
                      label={t(`billing.b2b.status.${inv.status}`, { defaultValue: inv.status })}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          )}
        </Table>
      </TableWrap>
      {!query.isLoading && rows.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={FileText} title={t('billing.b2b.empty')} />
        </div>
      ) : null}
    </div>
  );
}
